import { cn } from '@workspace/ui/lib/utils'
import { Play } from 'lucide-react'
import { useEffect, useRef } from 'react'

export interface VideoThumbnailProps {
  /** Video source URL — when provided, renders an inline muted preview */
  videoUrl?: string | null
  /** Duration badge label (e.g. "32:15") */
  duration?: string
  /** Whether the fullscreen modal is currently open — pauses preview */
  modalOpen?: boolean
  /** Time (seconds) to seek to when modal closes — preview stays paused */
  resumeTime?: number
  /** Called on click with the current preview time */
  onPlay?: (currentTime: number) => void
  /** Placeholder content when no videoUrl is available */
  placeholder?: React.ReactNode
  /** Additional classes for the outer button */
  className?: string
}

export function VideoThumbnail({
  videoUrl,
  duration,
  modalOpen,
  resumeTime,
  onPlay,
  placeholder,
  className,
}: VideoThumbnailProps) {
  const previewRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!previewRef.current) return
    if (modalOpen) {
      previewRef.current.pause()
    } else if (resumeTime != null) {
      previewRef.current.currentTime = resumeTime
      previewRef.current.pause()
    }
  }, [modalOpen, resumeTime])

  const handleClick = () => {
    const time = previewRef.current?.currentTime ?? 0
    onPlay?.(time)
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!onPlay}
      className={cn(
        'group relative flex cursor-pointer items-center justify-center overflow-hidden bg-gradient-to-br from-card to-background',
        className
      )}
    >
      {videoUrl ? (
        <video
          ref={previewRef}
          src={videoUrl}
          muted
          autoPlay
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        placeholder
      )}
      <div className="relative z-10 flex size-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-transform group-hover:scale-110">
        <Play className="size-5 fill-current text-white" />
      </div>
      {duration && (
        <span className="absolute right-2 bottom-2 z-10 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
          {duration}
        </span>
      )}
    </button>
  )
}
